import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import commonFilterIcon from '../../assets/images/commonfiltericon.svg';
import { getLocationId, getLocationsWithZones } from './zoneIndexGridUtils';

export const EMPTY_ZONE_INDEX_FILTERS = {
  locations: [],
  soldOut: [],
};

const SOLD_OUT_OPTIONS = [{ id: 'yes', label: 'Yes' }];

export default function ZoneIndexFilterPopup({
  locations = [],
  appliedFilters = EMPTY_ZONE_INDEX_FILTERS,
  onApply,
}) {
  const { t } = useTranslation();
  const popupRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(appliedFilters);

  const locationOptions = useMemo(
    () =>
      getLocationsWithZones(locations).map((location) => ({
        id: getLocationId(location),
        name: location?.name || '-',
      })),
    [locations]
  );

  const activeCount =
    draftFilters.locations.length + draftFilters.soldOut.length;

  const appliedFilterCount =
    (appliedFilters.locations?.length || 0) +
    (appliedFilters.soldOut?.length || 0);

  const toggleDraftValue = (category, value) => {
    setDraftFilters((prev) => {
      const current = prev[category] || [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [category]: next };
    });
  };

  const handleSelectAll = (category, options) => {
    setDraftFilters((prev) => {
      const optionIds = options.map((option) => option.id);
      const isAllSelected = optionIds.every((id) =>
        (prev[category] || []).includes(id)
      );
      return {
        ...prev,
        [category]: isAllSelected ? [] : optionIds,
      };
    });
  };

  const handleClearAll = () => {
    setDraftFilters(EMPTY_ZONE_INDEX_FILTERS);
  };

  const handleApply = () => {
    onApply?.({
      locations: [...draftFilters.locations],
      soldOut: [...draftFilters.soldOut],
    });
    setIsOpen(false);
  };

  const togglePopup = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    setDraftFilters({
      locations: [...(appliedFilters.locations || [])],
      soldOut: [...(appliedFilters.soldOut || [])],
    });
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const renderCategory = (categoryKey, title, options) => (
    <div className="zone-index__filter-category">
      <div className="zone-index__filter-category-header">
        <span className="zone-index__filter-category-title">{t(title)}</span>
        <button
          type="button"
          className="zone-index__filter-select-all"
          onClick={() => handleSelectAll(categoryKey, options)}
        >
          {t('select all')}
        </button>
      </div>
      <div className="zone-index__filter-pills">
        {options.map((option) => {
          const isSelected = (draftFilters[categoryKey] || []).includes(
            option.id
          );
          return (
            <button
              key={option.id}
              type="button"
              className={`zone-index__filter-pill${
                isSelected ? ' zone-index__filter-pill--selected' : ''
              }`}
              onClick={() => toggleDraftValue(categoryKey, option.id)}
            >
              {option.label ? t(option.label) : option.name}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="zone-index__filter-wrap" ref={popupRef}>
      <button
        type="button"
        className="zone-index__icon-btn zone-index__filter-btn"
        onClick={togglePopup}
        aria-label={
          appliedFilterCount > 0
            ? t('Filter ({{count}} active)', { count: appliedFilterCount })
            : t('Filter')
        }
        aria-expanded={isOpen}
      >
        <img src={commonFilterIcon} alt="" aria-hidden />
        {appliedFilterCount > 0 ? (
          <span className="zone-index__filter-btn-badge">{appliedFilterCount}</span>
        ) : null}
      </button>

      {isOpen ? (
        <div className="zone-index__filter-popup" role="dialog" aria-label={t('Filter')}>
          <div className="zone-index__filter-popup-title">{t('Filter')}</div>

          <div className="zone-index__filter-popup-divider" />

          <div className="zone-index__filter-active-row">
            <span className="zone-index__filter-active-count">
              {t('Active Filters')} ({activeCount})
            </span>
            <button
              type="button"
              className="zone-index__filter-clear-all"
              onClick={handleClearAll}
            >
              {t('Clear all')}
            </button>
          </div>

          <div className="zone-index__filter-popup-divider" />

          <div className="zone-index__filter-popup-body">
            {renderCategory('locations', 'Location', locationOptions)}
            {renderCategory('soldOut', 'Sold out', SOLD_OUT_OPTIONS)}
          </div>

          <button
            type="button"
            className="zone-index__filter-apply-btn"
            onClick={handleApply}
          >
            {t('Apply')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
